import React, { useEffect, useState } from "react";
import { Table, Input, Select } from "antd";
import FETCH from "../../../utils/fetch";
import NewLoader from "../../../components/styled-components/loader/NewLoader";
import swal from "sweetalert";
import { ToolTipCreator } from "../../../helpers/ToolTipCreator";

const { Option } = Select;

export const AssetAssignRetirement = ({
  rows,
  data,
  handleCashModuleCreate,
  cbFormChange,
  allLiabilities,
  attributeValues,
}) => {
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [currentPageIlliquid, setCurrentPageIlliquid] = useState(1);
  const [totalPagesIlliquid, setTotalPagesIlliquid] = useState(1);
  const [currentPageLiquid, setCurrentPageLiquid] = useState(1);
  const [totalPagesLiquid, setTotalPagesLiquid] = useState(1);

  // Data states
  const [illiquidAssetsList, setIlliquidAssetsList] = useState([]);
  const [liquidAssetsList, setLiquidAssetsList] = useState([]);
  const [dataSource, setDataSource] = useState(rows || []);

  const [cachedDataIlliquid, setCachedDataIlliquid] = useState({});
  const [cachedDataLiquid, setCachedDataLiquid] = useState({});

  useEffect(() => {
    setDataSource(rows || []);
  }, [rows]);

  // Fetching Illiquid Assets
  const getAssetsList = async (page) => {
    if (cachedDataIlliquid[page]) {
      updateIlliquidState(cachedDataIlliquid[page]);
      return;
    }
    try {
      setLoading(true);
      const id = JSON.parse(localStorage.getItem("accessId"));
      const userRole = JSON.parse(localStorage.getItem("role"));
      let url = "client-module-service/client-module/illiquid";
      let params = userRole === "Financial Advisor Client"
        ? `?page=${page}&limit=10&clientId=${id}`
        : `?page=${page}&limit=10`;

      let res = await FETCH.post({ url, id: params, body: {} });
      console.log("Fetched Illiquid Assets:", res.records);
      setLoading(false);
      if (res && res.records) {
        setCachedDataIlliquid(prev => ({ ...prev, [page]: res }));
        updateIlliquidState(res);
      }
    } catch (error) {
      console.error("Error fetching illiquid assets:", error);
      setLoading(false);
    }
  };

  // Fetching Liquid Assets
  const getAssetsLiquidList = async (page) => {
    if (cachedDataLiquid[page]) {
      updateLiquidState(cachedDataLiquid[page]);
      return;
    }
    try {
      setLoading(true);
      const id = JSON.parse(localStorage.getItem("accessId"));
      const userRole = JSON.parse(localStorage.getItem("role"));
      let url = "client-module-service/client-module/liquid";
      let params = userRole === "Financial Advisor Client"
        ? `?page=${page}&limit=10&clientId=${id}`
        : `?page=${page}&limit=10`;

      let res = await FETCH.post({ url, id: params, body: {} });
      console.log("Fetched Liquid Assets:", res.records);
      setLoading(false);
      if (res && res.records) {
        setCachedDataLiquid(prev => ({ ...prev, [page]: res }));
        updateLiquidState(res);
      }
    } catch (error) {
      console.error("Error fetching liquid assets:", error);
      setLoading(false);
    }
  };

  const updateIlliquidState = (datares) => {
    const assets = (datares.records || []).map(asset => ({
      ...asset,
      "Monetary Value": asset["Monetary Value"] || calculateMonetoryValue(asset),
      annualSavings: asset.annualSavings || 0,
      rateOfReturn: asset.rateOfReturn || (asset["Static"] ? parseFloat(asset["Static"]) : 0),
      Strategy: asset.Strategy || "Keep (Legacy)",
    }));
    setIlliquidAssetsList(assets);
    setTotalPagesIlliquid(Math.ceil(datares.pagination.total / (datares.pagination.limit || 10)));
  };

  const updateLiquidState = (datares) => {
    const assets = (datares.records || []).map(asset => ({
      ...asset,
      "Monetary Value": asset["Monetary Value"] || calculateMonetoryValue(asset),
      Strategy: asset.Strategy || "Sell to Fund",
      rateOfReturn: asset.rateOfReturn || (asset["Static"] ? parseFloat(asset["Static"]) : 0),
    }));
    setLiquidAssetsList(assets);
    setTotalPagesLiquid(Math.ceil(datares.pagination.total / (datares.pagination.limit || 10)));
  };

  useEffect(() => {
    getAssetsList(currentPageIlliquid);
  }, [currentPageIlliquid]);

  useEffect(() => {
    getAssetsLiquidList(currentPageLiquid);
  }, [currentPageLiquid]);

  // Calculations
  function calculateMonetoryValue(asset) {
    const goalDate = data["Goal Date"] ? new Date(data["Goal Date"]) : new Date();
    const startDate = asset["Cost Basis Date"] ? new Date(asset["Cost Basis Date"]) : new Date();
    const diffInMs = goalDate.getTime() - startDate.getTime();
    const years = diffInMs / (1000 * 60 * 60 * 24 * 365);

    let cost = parseFloat(asset["Cost Basis"]) || 0;
    let rate = (parseFloat(asset["Static"]) || 0) / 100;

    const value = cost * Math.pow(1 + rate, years);
    return isNaN(value) ? 0 : parseFloat(value.toFixed(2));
  }

  const calculateFV = (record) => {
    const currentAge = parseFloat(data["Current Age"]) || 0;
    const retirementAge = parseFloat(data["Desired Retirement Age"]) || 65;
    const t = Math.max(0, retirementAge - currentAge);
    const PV = parseFloat(record["Monetary Value"]) || 0;
    const r = (parseFloat(record.rateOfReturn) || 0) / 100;
    const PMT = parseFloat(record.annualSavings) || 0;

    if (r === 0) {
      return PV + (PMT * t);
    }
    // FV = PV*(1+r)^t + PMT * [((1+r)^t - 1) / r]
    const fv = PV * Math.pow(1 + r, t) + PMT * ((Math.pow(1 + r, t) - 1) / r);
    return fv.toFixed(2);
  };

  const handleInputChange = (id, field, value, listType) => {
    let finalValue = value;

    // Validation: Rate of return cannot exceed 100%
    if (field === "rateOfReturn") {
      finalValue = parseFloat(value);
      if (isNaN(finalValue)) finalValue = 0;
      if (finalValue > 100) finalValue = 100;
      if (finalValue < 0) finalValue = 0;
    }

    // Validation: Annual savings should be positive number
    if (field === "annualSavings") {
      finalValue = parseFloat(value);
      if (isNaN(finalValue)) finalValue = 0;
      if (finalValue < 0) finalValue = 0;
    }

    console.log(`Input Change: ${field} = ${finalValue} for asset ${id}`);
    const setter = listType === "liquid" ? setLiquidAssetsList : setIlliquidAssetsList;
    const list = listType === "liquid" ? liquidAssetsList : illiquidAssetsList;

    const updatedList = list.map(item => {
      if (item.id === id) {
        return { ...item, [field]: finalValue };
      }
      return item;
    });
    setter(updatedList);

    // Sync with dataSource if already selected
    if (dataSource.some(item => item.assetId === id)) {
      const updatedDataSource = dataSource.map(item => {
        if (item.assetId === id) {
          const matchedAsset = updatedList.find(a => a.id === id);
          const fv = calculateFV(matchedAsset);
          const commission = parseFloat(matchedAsset["Commission on Sale"]) || 0;
          const otherCosts = parseFloat(matchedAsset["Other Transaction Costs"]) || 0;
          const netLumpSum = matchedAsset.Strategy === "Sell to Fund" ? (parseFloat(fv) - commission - otherCosts).toFixed(2) : 0;

          const amountUse = matchedAsset.Strategy === "Sell to Fund" ? parseFloat(netLumpSum) : 0;

          return {
            ...item,
            [field]: finalValue,
            futureValue: fv,
            netLumpSum: netLumpSum,
            amountUseForGoal: amountUse,
            Strategy: matchedAsset.Strategy,
            rateOfReturn: matchedAsset.rateOfReturn,
            annualSavings: matchedAsset.annualSavings
          };
        }
        return item;
      });
      setDataSource(updatedDataSource);
      cbFormChange(updatedDataSource);
    }
  };

  const handleAdd = (record, listType) => {
    if (dataSource.some(item => item.assetId === record.id)) {
      swal("Info", "This asset has already been added.", "info");
      return;
    }

    const fv = calculateFV(record);
    const commission = parseFloat(record["Commission on Sale"]) || 0;
    const otherCosts = parseFloat(record["Other Transaction Costs"]) || 0;
    const netLumpSum = record.Strategy === "Sell to Fund" ? (parseFloat(fv) - commission - otherCosts).toFixed(2) : 0;
    const amountUse = record.Strategy === "Sell to Fund" ? parseFloat(netLumpSum) : 0;

    const newData = {
      key: (dataSource.length + 1).toString(),
      assetId: record.id,
      priority: dataSource.length + 1,
      assetName: record["Name of Asset"],
      asset: record["Name of Asset"], // Keeping for backward compatibility
      valueOfAssetGoalRate: record["Monetary Value"],
      annualSavings: record.annualSavings || 0,
      rateOfReturn: record.rateOfReturn || 0,
      Strategy: record.Strategy || "Keep (Legacy)",
      futureValue: fv,
      netLumpSum: netLumpSum,
      amountUseForGoal: amountUse,
      listType: listType
    };

    const updatedDataSource = [...dataSource, newData];
    setDataSource(updatedDataSource);
    cbFormChange(updatedDataSource);
    console.log("Adding Asset to Retirement Goal Assignment:", {
      id: record.id,
      name: record["Name of Asset"],
      strategy: record.Strategy,
      fv: fv,
      amountUsed: amountUse,
      mappedPayload: newData
    });
  };

  const handleDelete = (record) => {
    console.log("Removing Asset from Retirement Goal Assignment:", record.id);
    const updatedDataSource = dataSource.filter(item => item.assetId !== record.id);
    setDataSource(updatedDataSource);
    cbFormChange(updatedDataSource);
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null || isNaN(val)) return "$0.00";
    return "$" + parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Columns for Liquid Assets
  const colsLiquidAssets = [
    {
      title: "Asset Name (Type)",
      dataIndex: "Name of Asset",
      render: (text) => text || "--------",
    },
    {
      title: "Current Balance",
      dataIndex: "Monetary Value",
      render: (val) => formatCurrency(val),
    },
    {
      title: "Annual Savings (You + Employer)",
      render: (record) => (
        <Input
          type="number"
          value={record.annualSavings}
          onChange={(e) => handleInputChange(record.id, "annualSavings", e.target.value, "liquid")}
          prefix="$"
        />
      ),
    },
    {
      title: "Est. Return",
      render: (record) => (
        <Input
          type="number"
          value={record.rateOfReturn}
          onChange={(e) => handleInputChange(record.id, "rateOfReturn", e.target.value, "liquid")}
          suffix="%"
        />
      ),
    },
    {
      title: "Future Value (at Age 65)",
      render: (record) => formatCurrency(calculateFV(record)),
    },
    {
      title: "Select",
      render: (record) => {
        const isSelected = dataSource.some(item => item.assetId === record.id);
        return (
          <button
            className="saveStatusbutton"
            style={{
              width: "100%",
              fontWeight: "bold",
              background: isSelected ? "#E13C25" : "",
            }}
            onClick={(e) => {
              e.preventDefault();
              isSelected ? handleDelete(record) : handleAdd(record, "liquid");
            }}
          >
            {isSelected ? "Selected" : "Select"}
          </button>
        );
      },
    },
  ];

  // Columns for Illiquid Assets
  const colsNonLiquidAssets = [
    {
      title: "Asset Name",
      dataIndex: "Name of Asset",
      render: (text) => text || "--------",
    },
    {
      title: "Current Value",
      dataIndex: "Monetary Value",
      render: (val) => formatCurrency(val),
    },
    {
      title: "Strategy",
      render: (record) => (
        <Select
          value={record.Strategy}
          style={{ width: "100%" }}
          onChange={(val) => handleInputChange(record.id, "Strategy", val, "illiquid")}
        >
          <Option value="Keep (Legacy)">Keep (Legacy)</Option>
          <Option value="Sell to Fund">Sell to Fund</Option>
        </Select>
      ),
    },
    {
      title: "Sale Details",
      render: (record) => {
        if (record.Strategy === "Sell to Fund") {
          const fv = calculateFV(record);
          const commission = parseFloat(record["Commission on Sale"]) || 0;
          const otherCosts = parseFloat(record["Other Transaction Costs"]) || 0;
          const netLumpSum = parseFloat(fv) - commission - otherCosts;
          return (
            <div>
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>Net Lump Sum:</div>
              {formatCurrency(netLumpSum)}
            </div>
          );
        }
        return "N/A";
      },
    },
    {
      title: "Select",
      render: (record) => {
        const isSelected = dataSource.some(item => item.assetId === record.id);
        return (
          <button
            className="saveStatusbutton"
            style={{
              width: "100%",
              fontWeight: "bold",
              background: isSelected ? "#E13C25" : "",
            }}
            onClick={(e) => {
              e.preventDefault();
              isSelected ? handleDelete(record) : handleAdd(record, "illiquid");
            }}
          >
            {isSelected ? "Selected" : "Select"}
          </button>
        );
      },
    },
  ];

  return (
    <div className="assetAssignTable" style={{ position: "relative" }}>
      <div className="goalSubFormTable">
        <div style={{ display: "flex", alignItems: "baseline", columnGap: "3px" }}>
          <h3 className="incomecardheading">Liquid Assets</h3>
          <ToolTipCreator name={"Goals_Liquid_Assets"} />
        </div>
        <Table
          columns={colsLiquidAssets}
          dataSource={liquidAssetsList}
          rowKey="id"
          pagination={{
            total: totalPagesLiquid * 10,
            current: currentPageLiquid,
            onChange: (page) => setCurrentPageLiquid(page),
            pageSize: 10,
          }}
        />
      </div>

      <div className="goalSubFormTable" style={{ marginTop: "2rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", columnGap: "3px" }}>
          <h3 className="incomecardheading">Illiquid Assets</h3>
          <ToolTipCreator name={"Goals_Illiquid_Assets"} />
        </div>
        <Table
          columns={colsNonLiquidAssets}
          dataSource={illiquidAssetsList}
          rowKey="id"
          pagination={{
            total: totalPagesIlliquid * 10,
            current: currentPageIlliquid,
            onChange: (page) => setCurrentPageIlliquid(page),
            pageSize: 10,
          }}
        />
      </div>

      <NewLoader isLoading={loading} />
    </div>
  );
};
